import type { AskHelixNumericBindingDto } from '../../ask-helix';

/** Supplemental server-authored comparison; signed difference is left minus right. */
export type SynergyReconciliation = Readonly<{
  commitmentRef: string;
  asOf: string;
  reason: string;
  left: AskHelixNumericBindingDto;
  right: AskHelixNumericBindingDto;
  difference: AskHelixNumericBindingDto;
  /** Native published gap retained for audit; displayed delta is between the cent operands. */
  publishedGap?: Readonly<{ exactValue: string; valuePath: string }>;
  owner: string | null;
  /** Loop-panel labels this sentence replaces for its commitment (a fired detector rule). */
  supersedes?: readonly import('./synergy-loop.v1').SynergyLoopMeasure[];
  sentence: string;
  sourceLine: string;
}>;

const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Validate transport form only; this is not another numeric admission seam. */
export function readSynergyReconciliations(value: unknown): readonly SynergyReconciliation[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is SynergyReconciliation => record(entry)
    && ['commitmentRef', 'asOf', 'reason', 'sentence', 'sourceLine'].every(key =>
      typeof entry[key] === 'string' && entry[key] !== '')
    && (entry.owner === null || typeof entry.owner === 'string')
    && ['left', 'right', 'difference'].every(key => record(entry[key])))
    .map(entry => entry.supersedes === undefined || isTextList(entry.supersedes) ? entry
      : withoutSupersedes(entry));
}

const isTextList = (value: unknown): boolean =>
  Array.isArray(value) && value.every(item => typeof item === 'string');

function withoutSupersedes(entry: SynergyReconciliation): SynergyReconciliation {
  const { supersedes: _dropped, ...rest } = entry;
  return rest;
}
